import React, { useRef, useEffect } from "react";
import {
  animateTitle,
  animateImage,
  animateBio,
  animateSocial,
  cleanupScrollTriggers,
} from "./gsap";
import { InstagramLogo, YoutubeLogo, TwitterLogo } from "@phosphor-icons/react";

const FounderSection: React.FC = () => {
  const sectionRef = useRef<HTMLDivElement>(null);
  const titleRef = useRef<HTMLHeadingElement>(null);
  const bioRef = useRef<HTMLDivElement>(null);
  const imageRef = useRef<HTMLDivElement>(null);
  const socialRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    // Apply animations with a small timeout to ensure DOM is ready
    const animationTimeout = setTimeout(() => {
      if (
        sectionRef.current &&
        titleRef.current &&
        bioRef.current &&
        imageRef.current &&
        socialRef.current
      ) {
        // Animate the section elements
        animateTitle(
          { current: titleRef.current },
          { current: sectionRef.current }
        );
        animateImage(
          { current: imageRef.current },
          { current: sectionRef.current }
        );
        animateBio(
          { current: bioRef.current },
          { current: sectionRef.current }
        );
        animateSocial(
          { current: socialRef.current },
          { current: sectionRef.current }
        );
      }
    }, 100);

    // Cleanup animations and timeout on unmount
    return () => {
      clearTimeout(animationTimeout);
      cleanupScrollTriggers();
    };
  }, []);

  return (
    <div ref={sectionRef} className="py-16 bg-gray-50">
      <div className="container mx-auto px-4">
        <h2
          ref={titleRef}
          className="text-3xl md:text-4xl font-bold mb-12 text-center"
        >
          Lonny the Street Lawyer
        </h2>

        <div className="flex flex-col lg:flex-row items-center gap-10 mb-8">
          <div ref={imageRef} className="w-full lg:w-1/3">
            <div className="rounded-lg overflow-hidden shadow-xl mb-6 max-w-md mx-auto">
              <div
                className="relative w-full"
                style={{ paddingBottom: "56.25%" }}
              >
                <iframe
                  className="absolute top-0 left-0 w-full h-full"
                  src="https://www.youtube.com/embed/Q1sanwu25cM?si=UFElJO36UZE10cW2"
                  title="Lonny the Street Lawyer - Founder of Street Lawyer Services"
                  frameBorder="0"
                  allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
                  allowFullScreen
                  loading="lazy"
                ></iframe>
              </div>
            </div>

            <div ref={socialRef} className="flex flex-col items-center">
              <h3 className="font-semibold text-xl mb-3">Follow Lonny</h3>
              <div className="flex space-x-4">
                <a
                  href="https://www.instagram.com/lonnythestreetlawyer/"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-cannabis-green-500 hover:text-cannabis-green-600 transition-colors"
                  aria-label="Follow Lonny on Instagram"
                >
                  <InstagramLogo size={28} weight="fill" />
                </a>
                <a
                  href="https://www.youtube.com/@streetlawyerpodcast"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-cannabis-green-500 hover:text-cannabis-green-600 transition-colors"
                  aria-label="Subscribe to Lonny's YouTube Channel"
                >
                  <YoutubeLogo size={28} weight="fill" />
                </a>
                <a
                  href="https://twitter.com/DCStreetLawyer"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-cannabis-green-500 hover:text-cannabis-green-600 transition-colors"
                  aria-label="Follow Lonny on Twitter"
                >
                  <TwitterLogo size={28} weight="fill" />
                </a>
              </div>
            </div>
          </div>

          <div ref={bioRef} className="lg:w-2/3">
            <p className="text-gray-700 mb-4">
              Lonny, better known as "Lonny the Street Lawyer," is a dynamic
              figure in both the legal and cannabis industries. A Miami native,
              Lonny's illustrious educational journey led him from Stanford
              University to Columbia Law School, forging a path that would see
              him become a high-profile criminal defense attorney and a
              pioneering cannabis activist. Now residing primarily in the DMV
              area, Lonny's commitment to justice and entrepreneurial spirit
              have seen him represent over 5,000 clients facing serious criminal
              charges, from drug offenses to murder.
            </p>
            <p className="text-gray-700 mb-4">
              Beyond the courtroom, Lonny has made substantial inroads into
              media and business, earning a reputation as a charismatic radio
              personality and the visionary behind the East Coast cannabis
              brand, Street Lawyer Services. This venture not only markets his
              legal expertise but also embodies a lifestyle, championing
              cannabis culture and pushing for legal reforms through a unique
              blend of advocacy and enterprise.
            </p>
            <p className="text-gray-700 mb-4">
              Lonny's podcast, a candid platform diving into topics as diverse
              as street culture, politics, and personal growth, reflects his
              multifaceted interests and his dedication to sparking meaningful
              conversations. With a philosophy that no subject is too taboo, he
              invites a wide range of voices to explore the intersections of
              law, society, and the burgeoning cannabis industry.
            </p>
            <p className="text-gray-700 mb-4">
              As a figure at the forefront of cannabis legalization and a vocal
              advocate for reform, Lonny is more than just an attorney or
              entrepreneur; he is a cultural icon and a beacon for progressive
              change. Whether defending clients in court, engaging listeners on
              air, or guiding his business ventures towards new horizons, Lonny
              the Street Lawyer remains a formidable advocate for justice and a
              trailblazer in the cannabis movement.
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default FounderSection;
